import AddIcon from "@mui/icons-material/Add";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import InfoIcon from "@mui/icons-material/Info";
import NearMeIcon from "@mui/icons-material/NearMe";
import PolylineIcon from "@mui/icons-material/Polyline";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { Polygon } from "geojson";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useActiveFBOs } from "../../../containers/ActiveFBOContainer";
import { Hangar, HangarBay, Obstacle, ObstacleType } from "../../../types";
import { uuidv4 } from "../../../utils";
import { MetricAwareTextField } from "../../../widgets/MetricAwareTextField/MetricAwareTextField";
import Canvas from "../../../widgets/useCanvas";
import { DraggableGarageDoor } from "./DraggableGarageDoor";
import { DraggableObstacle } from "./DraggableObstacle";
import { DraggablePolygonObstacle } from "./DraggablePolygonObstacle";
import {
  clearBackgroundForObstacles,
  drawBayBoundaries,
  drawCurrentPolygon,
  drawDistanceMarkers,
  drawDoors,
  drawGarageDoorMarkers,
  drawSpacingBuffers,
  drawUncoveredAreas,
  drawUnobstructiveObstacles,
} from "./ObstacleCanvasDrawers";
import {
  getPolygonBounds,
  getPolygonGeometry,
  isCircleObstacle,
  isPolygonObstacle,
  Point,
} from "./obstacleUtils";
import { CircleObstacleDialog } from "./CircleObstacleDialog";
import { DraggableCircleObstacle } from "./DraggableCircleObstacle";
import { PolygonObstacleDialog } from "./PolygonObstacleDialog";

interface IObstacle {
  x: number;
  y: number;
  width: number;
  depth: number;
}

type ObstacleSpacing = {
  left?: number;
  right?: number;
  top?: number;
  bottom?: number;
};

export const getObstacleGeometry = (
  obstacle: IObstacle,
  spacing: ObstacleSpacing = {}
): Polygon => {
  return {
    type: "Polygon",
    coordinates: [
      [
        // top left
        [obstacle.x - (spacing.left ?? 0), obstacle.y - (spacing.top ?? 0)],
        // top right
        [
          obstacle.x + obstacle.width + (spacing.right ?? 0),
          obstacle.y - (spacing.top ?? 0),
        ],
        // bottom right
        [
          obstacle.x + obstacle.width + (spacing.right ?? 0),
          obstacle.y + obstacle.depth + (spacing.bottom ?? 0),
        ],
        // bottom left
        [
          obstacle.x - (spacing.left ?? 0),
          obstacle.y + obstacle.depth + (spacing.bottom ?? 0),
        ],
        // top left
        [obstacle.x - (spacing.left ?? 0), obstacle.y - (spacing.top ?? 0)],
      ],
    ],
  } as Polygon;
};

type ObstacleToolbarProps = {
  hangar: Hangar;
  onAdd: (obstacle: Obstacle) => void;
};

export const ObstacleToolbar: React.FC<ObstacleToolbarProps> = ({
  hangar,
  onAdd,
}) => {
  const { t } = useTranslation();
  const { activeFBO } = useActiveFBOs();
  const [open, setOpen] = React.useState<boolean>(false);
  const [obstacle, setObstacle] = React.useState<Obstacle>();

  React.useEffect(() => {
    setObstacle({
      fbo_id: activeFBO.id,
      hangar_id: hangar.id,
      id: uuidv4(),
      x: 0,
      y: 0,
      width: 20,
      depth: 20,
      height: null,
      geom: getObstacleGeometry({
        x: 0,
        y: 0,
        width: 20,
        depth: 20,
      }),
      type:
        activeFBO.subscription === "standard"
          ? ObstacleType.NO_TOW_AREA
          : ObstacleType.OBSTRUCTION,
    });
  }, [open]);

  const handleClose = () => {
    setOpen(false);
  };

  if (!obstacle) {
    return <div />;
  }

  const unit = activeFBO?.settings?.useMetricSystem ? "m" : "ft";

  return (
    <>
      <Stack
        direction="row"
        justifyContent="flex-end"
        spacing={1}
        sx={{ mb: 1 }}
      >
        <Stack direction="column" spacing={1}>
          <Tooltip title={t("settings.hangarSettings.obstacleToolbar.addObstacleTooltip")}>
            <Button
              variant="contained"
              color="info"
              startIcon={<AddIcon />}
              onClick={() => setOpen(true)}
            >
              {t("settings.hangarSettings.obstacleToolbar.addObstacleButton")}
            </Button>
          </Tooltip>
          <Stack direction="row" spacing={1} alignItems="center">
            <InfoIcon fontSize="small" />
            <Typography variant="caption">
              {t("settings.hangarSettings.obstacleToolbar.rightClickHint")}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>{t("settings.hangarSettings.obstacleToolbar.dialogTitle")}</DialogTitle>
        <DialogContent>
          <Stack direction="column" spacing={1}>
            <TextField
              fullWidth
              size="small"
              select
              required
              value={obstacle.type}
              onChange={(evt) =>
                setObstacle({
                  ...obstacle,
                  type: evt.target.value as ObstacleType,
                })
              }
            >
              <MenuItem
                value={ObstacleType.OBSTRUCTION}
                disabled={activeFBO.subscription === "standard"}
              >
                {t("settings.hangarSettings.obstacleToolbar.obstacleTypes.obstruction")}
              </MenuItem>
              <MenuItem value={ObstacleType.NO_TOW_AREA}>{t("settings.hangarSettings.obstacleToolbar.obstacleTypes.noTowArea")}</MenuItem>
              <MenuItem value={ObstacleType.UNOBSTRUCTIVE}>
                {t("settings.hangarSettings.obstacleToolbar.obstacleTypes.unobstructive")}
              </MenuItem>
              <MenuItem value={ObstacleType.CAUTION}>{t("settings.hangarSettings.obstacleToolbar.obstacleTypes.caution")}</MenuItem>
            </TextField>
            <MetricAwareTextField
              unit={activeFBO?.settings?.useMetricSystem ? "meters" : "feet"}
              type="number"
              fullWidth
              required
              inputProps={{
                min: 1,
                max: 1000,
                step: 0.1,
              }}
              size="small"
              onChange={(value) =>
                setObstacle({
                  ...obstacle,
                  width: value,
                })
              }
              value={obstacle.width}
              label={t("settings.hangarSettings.obstacleToolbar.labels.width", { unit })}
            />
            <MetricAwareTextField
              unit={activeFBO?.settings?.useMetricSystem ? "meters" : "feet"}
              type="number"
              fullWidth
              required
              inputProps={{
                min: 1,
                max: 1000,
                step: 0.1,
              }}
              size="small"
              onChange={(value) =>
                setObstacle({
                  ...obstacle,
                  depth: value,
                })
              }
              value={obstacle.depth}
              label={t("settings.hangarSettings.obstacleToolbar.labels.depth", { unit })}
            />
            <MetricAwareTextField
              unit={activeFBO?.settings?.useMetricSystem ? "meters" : "feet"}
              type="number"
              fullWidth
              inputProps={{
                min: 1,
                max: 100,
                step: 0.1,
              }}
              size="small"
              onChange={(value) =>
                setObstacle({
                  ...obstacle,
                  height: value,
                })
              }
              value={obstacle.height}
              label={t("settings.hangarSettings.obstacleToolbar.labels.height", { unit })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t("settings.hangarSettings.obstacleToolbar.buttons.cancel")}</Button>
          <div style={{ flex: "1 0 0" }} />
          <Button
            variant="contained"
            color="success"
            onClick={() => {
              onAdd({
                ...obstacle,
                geom: getObstacleGeometry(obstacle),
              });
              handleClose();
            }}
          >
            {t("settings.hangarSettings.obstacleToolbar.buttons.add")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export type ObstacleTool = "select" | "rectangle" | "polygon" | "circle";

type Props = {
  hangar: Hangar;
  setHangar: (h: Hangar) => void;
  referenceImage?: string;
  bays?: HangarBay[]; // NEW: Bay boundaries to visualize
  hideToolbar?: boolean; // Hide the Add Obstacle button (now in Features tab)
};

export const ObstacleCanvas: React.FC<Props> = ({
  hangar,
  setHangar,
  referenceImage,
  bays = [],
  hideToolbar = false,
}) => {
  const { t } = useTranslation();
  const { activeFBO } = useActiveFBOs();
  const [canvasHeight, setCanvasHeight] = React.useState<number>(0);

  // Tool selection and polygon drawing state
  const [selectedTool, setSelectedTool] = React.useState<ObstacleTool>("select");
  const [currentPolygon, setCurrentPolygon] = React.useState<Point[]>([]);
  const [mousePosition, setMousePosition] = React.useState<Point | null>(null);
  const [polygonDialogOpen, setPolygonDialogOpen] = React.useState(false);

  // Circle drawing state
  const [circleCenter, setCircleCenter] = React.useState<Point | null>(null);
  const [circleDialogOpen, setCircleDialogOpen] = React.useState(false);

  const div = React.useCallback(
    (node) => {
      if (node?.offsetWidth && node?.offsetHeight) {
        const maxHeight = Math.min(
          node.parentNode.parentNode.parentNode.offsetHeight - 100,
          // 999999999,
          node?.offsetWidth * (hangar.depth / hangar.width)
        );
        setCanvasHeight(maxHeight);
      }
    },
    [hangar?.width]
  );

  const canvasRef = React.createRef<HTMLCanvasElement>();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const canvasWidth = (canvasHeight * hangar?.width) / hangar?.depth;
  const feetToPixels = (feet: number) => (feet * canvasWidth) / hangar?.width;
  const pixelsToFeet = (pixels: number) => (pixels * hangar?.width) / canvasWidth;

  if (!hangar) {
    return null;
  }

  const draw = React.useCallback(
    (ctx: CanvasRenderingContext2D) => {
      ctx.clearRect(-1, -1, canvasWidth, canvasHeight);
      drawDoors(ctx, hangar, feetToPixels);
      drawSpacingBuffers(ctx, hangar, feetToPixels, {
        showWallSpacingFill: true, // Always show fill in the settings editor
      });
      // NEW: Draw bay boundaries with color-coding
      if (bays && bays.length > 0) {
        drawBayBoundaries(ctx, bays, hangar, feetToPixels, {
          showLabels: true,
          showHeights: true,
        });
        // Draw uncovered areas with hatching pattern
        drawUncoveredAreas(ctx, bays, hangar, feetToPixels);
      }
      drawUnobstructiveObstacles(ctx, hangar, feetToPixels);
      drawGarageDoorMarkers(
        ctx,
        hangar,
        hangar.garageDoors,
        feetToPixels,
        activeFBO?.settings?.useMetricSystem ? "m" : "ft"
      );
      drawDistanceMarkers(
        ctx,
        hangar,
        hangar.obstacles.find((o) => o.active),
        feetToPixels,
        activeFBO?.settings?.useMetricSystem ? "m" : "ft"
      );
      clearBackgroundForObstacles(ctx, hangar, feetToPixels);

      // Draw current polygon being created
      if (selectedTool === "polygon" && currentPolygon.length > 0) {
        drawCurrentPolygon(ctx, currentPolygon, mousePosition, feetToPixels);
      }
    },
    [hangar, bays, canvasHeight, feetToPixels, selectedTool, currentPolygon, mousePosition]
  );

  // Constrain point to 45° angle increments (for Shift+click)
  const constrainToAngle = (fromPoint: Point, toPoint: Point): Point => {
    const dx = toPoint.x - fromPoint.x;
    const dy = toPoint.y - fromPoint.y;
    const distance = Math.hypot(dx, dy);

    // Calculate angle and snap to nearest 45°
    const angle = Math.atan2(dy, dx);
    const snappedAngle = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4);

    return {
      x: fromPoint.x + distance * Math.cos(snappedAngle),
      y: fromPoint.y + distance * Math.sin(snappedAngle),
    };
  };

  const handleCanvasClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    let x = pixelsToFeet(event.clientX - rect.left);
    let y = pixelsToFeet(event.clientY - rect.top);

    // Handle circle tool - single click to set center
    if (selectedTool === "circle") {
      setCircleCenter({ x, y });
      setCircleDialogOpen(true);
      return;
    }

    // Handle polygon tool
    if (selectedTool !== "polygon") return;

    // Shift+click constrains to 45° angles
    if (event.shiftKey && currentPolygon.length > 0) {
      const lastPoint = currentPolygon[currentPolygon.length - 1];
      const constrained = constrainToAngle(lastPoint, { x, y });
      x = constrained.x;
      y = constrained.y;
    }

    // Check if clicking on first point to close polygon (>= 3 points)
    if (currentPolygon.length >= 3) {
      const firstPoint = currentPolygon[0];
      const closingThreshold = pixelsToFeet(15); // 15 pixels threshold
      if (Math.hypot(firstPoint.x - x, firstPoint.y - y) < closingThreshold) {
        // Close the polygon and open properties dialog
        setPolygonDialogOpen(true);
        return;
      }
    }

    // Add point to current polygon
    setCurrentPolygon([...currentPolygon, { x, y }]);
  };

  const handleMouseDown = () => {};

  const handleMouseMove = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (selectedTool !== "polygon" || currentPolygon.length === 0) {
      setMousePosition(null);
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    let x = pixelsToFeet(event.clientX - rect.left);
    let y = pixelsToFeet(event.clientY - rect.top);

    // Shift constrains preview to 45° angles
    if (event.shiftKey) {
      const lastPoint = currentPolygon[currentPolygon.length - 1];
      const constrained = constrainToAngle(lastPoint, { x, y });
      x = constrained.x;
      y = constrained.y;
    }

    setMousePosition({ x, y });
  };

  const handleMouseExit = () => {
    setMousePosition(null);
  };

  const handlePolygonSave = (obstacle: Obstacle) => {
    setHangar({ ...hangar, obstacles: [...hangar.obstacles, obstacle] });
    setCurrentPolygon([]);
    setPolygonDialogOpen(false);
    setSelectedTool("select");
  };

  const handlePolygonCancel = () => {
    setCurrentPolygon([]);
    setPolygonDialogOpen(false);
  };

  const handleCircleSave = (obstacle: Obstacle) => {
    setHangar({ ...hangar, obstacles: [...hangar.obstacles, obstacle] });
    setCircleCenter(null);
    setCircleDialogOpen(false);
    setSelectedTool("select");
  };

  const handleCircleCancel = () => {
    setCircleCenter(null);
    setCircleDialogOpen(false);
  };

  const handleToolChange = (
    _event: React.MouseEvent<HTMLElement>,
    newTool: ObstacleTool | null
  ) => {
    if (newTool !== null) {
      setSelectedTool(newTool);
      // Clear any in-progress polygon when switching tools
      if (newTool !== "polygon") {
        setCurrentPolygon([]);
        setMousePosition(null);
      }
      // Clear circle state when switching tools
      if (newTool !== "circle") {
        setCircleCenter(null);
      }
    }
  };

  const backgroundColor = Boolean(referenceImage) ? "transparent" : "#E8EBF1";
  const dashColor = "#ADBDD5";
  const dashWidth = 3;
  const gridSize = feetToPixels(10);
  const dashLength = gridSize / 10;

  return (
    <Stack direction="column" sx={{ width: "100%" }}>
      {!hideToolbar && (
        <Stack direction="column" spacing={1} sx={{ mb: 1 }}>
          {/* Tool Selection */}
          <Stack direction="row" spacing={2} alignItems="center">
            <ToggleButtonGroup
              value={selectedTool}
              exclusive
              onChange={handleToolChange}
              size="small"
            >
              <ToggleButton value="select">
                <Tooltip title={t("settings.hangarSettings.obstacleToolbar.tools.select", "Select")}>
                  <NearMeIcon />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="rectangle">
                <Tooltip title={t("settings.hangarSettings.obstacleToolbar.tools.rectangle", "Rectangle")}>
                  <CropSquareIcon />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="polygon">
                <Tooltip title={t("settings.hangarSettings.obstacleToolbar.tools.polygon", "Polygon")}>
                  <PolylineIcon />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="circle">
                <Tooltip title={t("settings.hangarSettings.obstacleToolbar.tools.circle", "Circle")}>
                  <CircleOutlinedIcon />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Show rectangle toolbar when rectangle tool is selected */}
            {selectedTool === "rectangle" && (
              <ObstacleToolbar
                hangar={hangar}
                onAdd={(obstacle) =>
                  setHangar({ ...hangar, obstacles: [...hangar.obstacles, obstacle] })
                }
              />
            )}
          </Stack>

          {/* Polygon drawing hints */}
          {selectedTool === "polygon" && (
            <Stack direction="row" spacing={1} alignItems="center">
              <InfoIcon fontSize="small" color="info" />
              <Typography variant="caption" color="text.secondary">
                {currentPolygon.length === 0
                  ? t("settings.hangarSettings.obstacleToolbar.polygonHints.clickToAddPoints", "Click to add points")
                  : currentPolygon.length < 3
                    ? t("settings.hangarSettings.obstacleToolbar.polygonHints.minThreePoints", "Add at least 3 points")
                    : t("settings.hangarSettings.obstacleToolbar.polygonHints.clickFirstPointToClose", "Click first point to close polygon")}
              </Typography>
              {currentPolygon.length > 0 && (
                <Button
                  size="small"
                  variant="text"
                  color="error"
                  onClick={() => setCurrentPolygon([])}
                >
                  {t("settings.hangarSettings.obstacleToolbar.buttons.cancel", "Cancel")}
                </Button>
              )}
            </Stack>
          )}

          {/* Circle drawing hints */}
          {selectedTool === "circle" && (
            <Stack direction="row" spacing={1} alignItems="center">
              <InfoIcon fontSize="small" color="info" />
              <Typography variant="caption" color="text.secondary">
                {t(
                  "settings.hangarSettings.obstacleToolbar.circleHints.clickToPlaceCenter",
                  "Click to place center point"
                )}
              </Typography>
            </Stack>
          )}

          {/* Right-click hint for select mode */}
          {selectedTool === "select" && (
            <Stack direction="row" spacing={1} alignItems="center">
              <InfoIcon fontSize="small" />
              <Typography variant="caption">
                {t("settings.hangarSettings.obstacleToolbar.rightClickHint")}
              </Typography>
            </Stack>
          )}
        </Stack>
      )}

      {/* Polygon properties dialog */}
      <PolygonObstacleDialog
        open={polygonDialogOpen}
        points={currentPolygon}
        hangar={hangar}
        onSave={handlePolygonSave}
        onClose={handlePolygonCancel}
      />

      {/* Circle properties dialog */}
      <CircleObstacleDialog
        open={circleDialogOpen}
        center={circleCenter}
        hangar={hangar}
        onSave={handleCircleSave}
        onClose={handleCircleCancel}
      />
      <Box
        ref={div}
        sx={{
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            // need to account for border here
            width: canvasWidth,
            height: canvasHeight,
            // border: `2px solid ${dashColor}`,
            // Allow clicks to pass through to canvas when drawing polygon or circle
            pointerEvents:
              selectedTool === "polygon" || selectedTool === "circle"
                ? "none"
                : "auto",
          }}
        >
          {hangar.obstacles
            .filter(() => {
              const activeGarageDoor = hangar.garageDoors.find((g) => g.active);
              return !Boolean(activeGarageDoor);
            })
            .map((obstacle) => {
              // Check for circle first (circles are a subset of polygons)
              const circleCheck = isCircleObstacle(obstacle);
              if (circleCheck.isCircle) {
                return (
                  <DraggableCircleObstacle
                    key={`obstacle-${obstacle.id}`}
                    hangar={hangar}
                    feetToPixels={feetToPixels(1)}
                    obstacle={obstacle}
                    isMetric={activeFBO?.settings?.useMetricSystem}
                    onDrag={(updatedObstacle) => {
                      setHangar({
                        ...hangar,
                        obstacles: hangar.obstacles.map((o) =>
                          o.id === updatedObstacle.id ? updatedObstacle : o
                        ),
                      });
                    }}
                    onDelete={(obstacleToDelete) =>
                      setHangar({
                        ...hangar,
                        obstacles: hangar.obstacles.filter(
                          (o) => o.id !== obstacleToDelete.id
                        ),
                      })
                    }
                    onCopy={(copiedObstacle) =>
                      setHangar({
                        ...hangar,
                        obstacles: [...hangar.obstacles, copiedObstacle],
                      })
                    }
                  />
                );
              }

              // Then check for polygon
              if (isPolygonObstacle(obstacle)) {
                return (
                  <DraggablePolygonObstacle
                    key={`obstacle-${obstacle.id}`}
                    hangar={hangar}
                    feetToPixels={feetToPixels(1)}
                    obstacle={obstacle}
                    isMetric={activeFBO?.settings?.useMetricSystem}
                    onDrag={(updatedObstacle) => {
                      setHangar({
                        ...hangar,
                        obstacles: hangar.obstacles.map((o) =>
                          o.id === updatedObstacle.id ? updatedObstacle : o
                        ),
                      });
                    }}
                    onDelete={(obstacleToDelete) =>
                      setHangar({
                        ...hangar,
                        obstacles: hangar.obstacles.filter(
                          (o) => o.id !== obstacleToDelete.id
                        ),
                      })
                    }
                    onCopy={(copiedObstacle) =>
                      setHangar({
                        ...hangar,
                        obstacles: [...hangar.obstacles, copiedObstacle],
                      })
                    }
                  />
                );
              }

              // Default: rectangle
              return (
                <DraggableObstacle
                  key={`obstacle-${obstacle.id}`}
                  hangar={hangar}
                  setHangar={setHangar}
                  feetToPixels={feetToPixels(1)}
                  obstacle={obstacle}
                  onDrag={(updatedObstacle) => {
                    setHangar({
                      ...hangar,
                      obstacles: hangar.obstacles.map((o) =>
                        o.id === updatedObstacle.id ? updatedObstacle : o
                      ),
                    });
                  }}
                  onDelete={(obstacleToDelete) =>
                    setHangar({
                      ...hangar,
                      obstacles: hangar.obstacles.filter(
                        (o) => o.id !== obstacleToDelete.id
                      ),
                    })
                  }
                />
              );
            })}
          {hangar.garageDoors
            .filter(() => {
              const activeObstacle = hangar.obstacles.find((o) => o.active);
              return !Boolean(activeObstacle);
            })
            .map((garageDoor) => (
              <React.Fragment key={`garageDoor-${garageDoor.id}`}>
                <DraggableGarageDoor
                  hangar={hangar}
                  feetToPixels={feetToPixels(1)}
                  garageDoor={garageDoor}
                  onDrag={(garageDoor) => {
                    setHangar({
                      ...hangar,
                      garageDoors: hangar.garageDoors.map((g) =>
                        g.id === garageDoor.id ? garageDoor : g
                      ),
                    });
                  }}
                  onDelete={(garageDoor) =>
                    setHangar({
                      ...hangar,
                      garageDoors: hangar.garageDoors.filter(
                        (g) => g.id !== garageDoor.id
                      ),
                    })
                  }
                />
              </React.Fragment>
            ))}
        </Box>
        <Box
          sx={{
            // https://s3-prod.dogtopia.com/wp-content/uploads/2019/03/0-570x380.jpg
            backgroundImage: `url(${referenceImage})`,
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
          }}
        >
          <Canvas
            ref={canvasRef}
            width={canvasWidth}
            height={canvasHeight}
            draw={draw}
            onClick={handleCanvasClick}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseExit}
            onMouseLeave={handleMouseExit}
            style={{
              backgroundColor,
              backgroundSize: `${dashLength * 2}px ${
                dashLength * 2
              }px, ${gridSize}px ${gridSize}px`,
              backgroundPosition: `${-dashWidth / 2}px ${-dashWidth / 2}px`,
              backgroundImage: `linear-gradient(to bottom, transparent 5px, ${backgroundColor} 5px),
              linear-gradient(to right, ${dashColor}50 3px, transparent 3px),
              linear-gradient(to right, transparent 5px, ${backgroundColor} 5px),
              linear-gradient(to bottom, ${dashColor}50 3px, transparent 3px)
            `,
              cursor:
                selectedTool === "polygon" || selectedTool === "circle"
                  ? "crosshair"
                  : "default",
            }}
          />
        </Box>
      </Box>
    </Stack>
  );
};
